
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Project, Estimate } from "@/types/project-types";
import { componentTypes } from "@/data/estimationData";
import { ComponentType } from "@/types/estimation-types";
import { updateEstimate, calculateTotals, generatePDF, exportToExcel } from "@/components/rfp/rfp-utils";
import AnalyticsTab from "@/components/rfp/AnalyticsTab";
import ReportTab from "@/components/rfp/ReportTab";
import ChatWidget from "@/components/chat/ChatWidget";
import ProjectHeader from "@/components/project/ProjectHeader";
import ProjectLoading from "@/components/project/ProjectLoading";
import ProjectNotFound from "@/components/project/ProjectNotFound";
import EstimationTabContent from "@/components/project/EstimationTabContent";

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState("estimate");
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectId) return;
    fetchProjectData();

    // Check URL params for actions
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    const tab = searchParams.get('tab');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    if (action === 'download') {
      // Schedule PDF download after data loads
      const timer = setTimeout(() => {
        handleGeneratePDF();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [projectId, location]);

  const fetchProjectData = async () => {
    setIsLoading(true);
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;
      if (!projectData) {
        navigate('/projects');
        toast({
          title: "Error",
          description: "Project not found",
          variant: "destructive",
        });
        return;
      }
      
      setProject(projectData as Project);

      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select('*')
        .eq('project_id', projectId);

      if (estimatesError) throw estimatesError;

      if (estimatesData && estimatesData.length > 0) {
        // Process the estimates to ensure both hours and days properties exist
        const updatedEstimates = estimatesData.map(est => {
          // Make sure we have both days and hours properties
          const estimate: Estimate = {
            ...est,
            // Ensure total_dev_days exists (convert from hours if needed)
            total_dev_days: est.total_dev_days !== undefined
              ? Number(est.total_dev_days)
              : (est.total_dev_hours ? Number(est.total_dev_hours) / 8 : 0),
            
            // Ensure total_days exists (convert from hours if needed)
            total_days: est.total_days !== undefined
              ? Number(est.total_days)
              : (est.total_hours ? Number(est.total_hours) / 8 : 0),
            
            // Make sure hours values also exist for backward compatibility
            total_dev_hours: est.total_dev_hours !== undefined
              ? Number(est.total_dev_hours)
              : (est.total_dev_days ? Number(est.total_dev_days) * 8 : 0),
            
            total_hours: est.total_hours !== undefined
              ? Number(est.total_hours)
              : (est.total_days ? Number(est.total_days) * 8 : 0)
          };
          return estimate;
        });
        
        setEstimates(updatedEstimates);
      } else {
        // Create default estimates for all component types
        const initialEstimates = componentTypes.map(type => ({
          project_id: projectId,
          component_type: type,
          simple_count: 0,
          medium_count: 0,
          complex_count: 0,
          supercomplex_count: 0,
          total_points: 0,
          total_days: 0,
          total_dev_days: 0,
          total_dev_hours: 0,
          total_hours: 0
        })) as Estimate[];
        
        setEstimates(initialEstimates);
      }

    } catch (error) {
      console.error('Error fetching project data:', error);
      toast({
        title: "Error",
        description: "Failed to load project data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEstimateUpdate = (index: number, field: string, value: number) => {
    // Convert Estimate[] to RFPEstimate[] to use the updateEstimate utility
    const rfpEstimates = estimates.map(est => ({
      rfp_id: est.project_id,
      component_type: est.component_type,
      simple_count: est.simple_count,
      medium_count: est.medium_count,
      complex_count: est.complex_count,
      supercomplex_count: est.supercomplex_count,
      total_points: est.total_points,
      total_dev_days: est.total_dev_days,
      total_days: est.total_days
    }));
    
    // Update the estimates using the utility function
    const updatedRfpEstimates = updateEstimate(rfpEstimates, index, field, value);
    
    // Convert back to Estimate[] and maintain other properties
    const newEstimates = updatedRfpEstimates.map((rfpEst, i) => {
      const originalEst = estimates[i];
      return {
        ...originalEst,
        simple_count: rfpEst.simple_count,
        medium_count: rfpEst.medium_count,
        complex_count: rfpEst.complex_count,
        supercomplex_count: rfpEst.supercomplex_count,
        total_points: rfpEst.total_points,
        total_dev_days: rfpEst.total_dev_days,
        total_days: rfpEst.total_days,
        total_dev_hours: rfpEst.total_dev_days * 8, // Convert days to hours
        total_hours: rfpEst.total_days * 8          // Convert days to hours
      };
    });
    
    setEstimates(newEstimates);
  };

  const saveEstimates = async () => {
    if (!projectId || !project) return;
    
    setIsSaving(true);
    try {
      // Update project version
      const newVersion = project.version + 1;
      await supabase
        .from('projects')
        .update({ version: newVersion, updated_at: new Date().toISOString() })
        .eq('id', projectId);
        
      setProject({
        ...project,
        version: newVersion,
        updated_at: new Date().toISOString()
      });

      // Delete existing estimates
      await supabase
        .from('estimates')
        .delete()
        .eq('project_id', projectId);
      
      // Insert updated estimates
      const estimatesToSave = estimates.map(est => ({
        project_id: projectId,
        component_type: est.component_type,
        simple_count: est.simple_count,
        medium_count: est.medium_count,
        complex_count: est.complex_count,
        supercomplex_count: est.supercomplex_count,
        total_points: est.total_points,
        total_dev_days: est.total_dev_days,
        total_days: est.total_days,
        total_dev_hours: est.total_dev_days * 8, // Convert days to hours for backwards compatibility
        total_hours: est.total_days * 8          // Convert days to hours for backwards compatibility
      }));

      const { error } = await supabase
        .from('estimates')
        .insert(estimatesToSave);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Estimates saved successfully",
      });
    } catch (error) {
      console.error('Error saving estimates:', error);
      toast({
        title: "Error",
        description: "Failed to save estimates",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePDF = () => {
    if (!project) return;
    generatePDF(reportRef, project.name);
  };

  const handleExportExcel = () => {
    if (!project) return;
    exportToExcel(rfpEstimates, project.name, {
      projectName: project.name,
      description: project.description || "",
      version: project.version,
      updatedAt: project.updated_at
    });
  };

  const sendEstimateEmail = async () => {
    // In a real implementation, this would call an Edge Function to send the email
    // For now, we'll simulate this with a timeout
    setIsSendingEmail(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: "Email Sent",
        description: "Estimate report has been emailed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Prepare data for reuse
  const rfpEstimates = estimates.map(est => ({
    rfp_id: est.project_id,
    component_type: est.component_type,
    simple_count: est.simple_count,
    medium_count: est.medium_count,
    complex_count: est.complex_count,
    supercomplex_count: est.supercomplex_count,
    total_points: est.total_points,
    total_dev_days: est.total_dev_days,
    total_days: est.total_days
  }));
  
  const totals = calculateTotals(rfpEstimates);

  if (isLoading) {
    return <ProjectLoading />;
  }

  if (!project) {
    return <ProjectNotFound />;
  }

  return (
    <div className="container max-w-6xl py-8 space-y-6 animate-fade-in">
      <ProjectHeader project={project} />

      <Tabs defaultValue="estimate" value={activeTab} className="w-full" onValueChange={(value) => setActiveTab(value)}>
        <TabsList className="grid w-full grid-cols-3 bg-purple-100 dark:bg-gray-700">
          <TabsTrigger 
            value="estimate" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600"
          >
            Estimation Tool
          </TabsTrigger>
          <TabsTrigger 
            value="analytics" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600"
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger 
            value="report" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white dark:data-[state=active]:bg-gray-600"
          >
            Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="estimate">
          <EstimationTabContent
            estimates={estimates}
            project={project}
            isSaving={isSaving}
            isSendingEmail={isSendingEmail}
            rfpEstimates={rfpEstimates}
            totals={totals}
            onEstimateUpdate={handleEstimateUpdate}
            onSaveEstimates={saveEstimates}
            onSendEmail={sendEstimateEmail}
            onGeneratePDF={handleGeneratePDF}
            onExportExcel={handleExportExcel}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab componentEstimates={rfpEstimates} />
        </TabsContent>

        <TabsContent value="report">
          <ReportTab 
            formData={{
              name: project?.name || "",
              description: project?.description || ""
            }}
            componentEstimates={rfpEstimates}
            version={project?.version || 1}
            updatedAt={project?.updated_at || ""}
          />
        </TabsContent>
      </Tabs>
      
      <ChatWidget />
    </div>
  );
};

export default ProjectDetail;
